import React, { useCallback, useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fechaLarga } from "@/lib/fechas";
import { useData } from "@/context/DataContext";
import { eur } from "@/lib/gastos";

const Btn = ({ children, onClick, tone = "white", testid, disabled }) => {
  const cls = { green: "bg-[#3A6B56] text-white", amber: "bg-[#FEF3C7] text-[#92400E]", blue: "bg-[#1C2421] text-white", white: "bg-white border border-[#E5DFD5] text-[#4A5550]" }[tone];
  return <button data-testid={testid} disabled={disabled} onClick={onClick} className={`min-h-[44px] px-3.5 rounded-xl font-semibold text-sm disabled:opacity-60 inline-flex items-center gap-1.5 ${cls}`}>{children}</button>;
};

export const mailtoDe = (o) => `mailto:${o.destinatarios.map((d) => d.email).join(",")}?subject=${encodeURIComponent(o.asunto || "")}&body=${encodeURIComponent(o.mensaje || "")}`;
const esIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const gmailDe = (o) => {
  const to = encodeURIComponent(o.destinatarios.map((d) => d.email).join(","));
  const su = encodeURIComponent(o.asunto || ""), body = encodeURIComponent(o.mensaje || "");
  return esIOS() ? `googlegmail:///co?to=${to}&subject=${su}&body=${body}` : `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${su}&body=${body}`;
};

// Una ocurrencia preparada: revisar/editar mensaje, abrir en el correo, realizada, posponer, omitir.
export const OcurrenciaCard = ({ o, onChange }) => {
  const [msg, setMsg] = useState(o.mensaje);
  const [asunto, setAsunto] = useState(o.asunto);
  const [editar, setEditar] = useState(false);
  const [pos, setPos] = useState(false);
  const { pushPrefs } = useData();
  const app = pushPrefs.app_correo || "ambas";
  const correo = o.modo !== "aviso" && o.destinatarios?.length > 0;
  const accion = async (a, extra = {}, ok) => {
    try {
      await api.post(`/gestiones/ocurrencias/${o.id}`, { accion: a, ...extra });
      if (ok) toast.success(ok);
      onChange();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar."); }
  };
  const abrir = async (gmail) => {
    if (editar) { await api.post(`/gestiones/ocurrencias/${o.id}`, { accion: "editar", mensaje: msg, asunto }); setEditar(false); }
    const url = (gmail ? gmailDe : mailtoDe)({ ...o, mensaje: msg, asunto });
    if (gmail && !esIOS()) window.open(url, "_blank", "noopener"); else window.location.href = url;
    accion("abierta");
  };
  const copiar = async () => {
    try { await navigator.clipboard.writeText(msg); toast.success("Mensaje copiado", { description: "Pégalo en tu correo o en WhatsApp." }); }
    catch { toast.error("No se pudo copiar. Mantén pulsado el texto para copiarlo."); }
  };
  return (
    <div data-testid={`ocurrencia-${o.id}`} className="bg-white border border-[#E5DFD5] rounded-2xl p-4 space-y-3">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#E8F0EC] flex items-center justify-center shrink-0"><Icons.Repeat size={18} className="text-[#3A6B56]" /></span>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-base text-[#1C2421]">{o.titulo}</p>
          {correo ? (<>
            <p className="text-sm text-[#4A5550]">Correo para: {o.destinatarios.map((d) => d.nombre).join(", ")}{o.importe ? ` · ${eur(o.importe)}` : ""}</p>
            <p className="text-sm text-[#1C2421] break-all" data-testid={`ocurrencia-email-${o.id}`}><Icons.AtSign size={13} className="inline -mt-0.5 text-[#6E7A74]" /> {o.destinatarios.map((d) => d.email).join(", ")}</p>
          </>) : o.importe ? <p className="text-sm text-[#4A5550]">Importe: {eur(o.importe)}</p> : null}
          <p className="text-xs text-[#6E7A74]">{fechaLarga(o.fecha)}{o.estado === "abierta" ? " · abierto en tu correo" : o.estado === "pospuesta" ? ` · pospuesta hasta ${fechaLarga(o.pospuesta_hasta)}` : ""}</p>
        </div>
      </div>
      {!correo ? null : editar ? (
        <div className="space-y-2">
          <input data-testid={`ocurrencia-asunto-${o.id}`} value={asunto} onChange={(e) => setAsunto(e.target.value)} className="w-full min-h-[44px] px-3 rounded-xl border border-[#E5DFD5] text-base" />
          <textarea data-testid={`ocurrencia-mensaje-${o.id}`} rows={4} value={msg} onChange={(e) => setMsg(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[#E5DFD5] text-base" />
          <Btn testid={`ocurrencia-guardar-${o.id}`} tone="green" onClick={async () => { await accion("editar", { mensaje: msg, asunto }, "Mensaje guardado"); setEditar(false); }}>Guardar cambios</Btn>
        </div>
      ) : (
        <button data-testid={`ocurrencia-editar-${o.id}`} onClick={() => setEditar(true)} className="w-full text-left bg-[#FAF8F5] rounded-xl px-3.5 py-2.5 text-base text-[#1C2421] whitespace-pre-line">
          {msg}<span className="block text-xs font-semibold text-[#3A6B56] mt-1"><Icons.Pencil size={12} className="inline -mt-0.5" /> Revisar y editar</span>
        </button>
      )}
      {pos ? (
        <div className="flex flex-wrap gap-2">
          {[[1, "Mañana"], [3, "3 días"], [7, "1 semana"]].map(([d, l]) => (
            <Btn key={d} tone="amber" testid={`ocurrencia-posponer-${d}-${o.id}`} onClick={() => { setPos(false); accion("posponer", { dias: d }, `Pospuesta: ${l.toLowerCase()}`); }}>{l}</Btn>
          ))}
          <Btn testid={`ocurrencia-posponer-volver-${o.id}`} onClick={() => setPos(false)}>Volver</Btn>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {correo && app !== "gmail" && <Btn tone="blue" testid={`ocurrencia-abrir-${o.id}`} onClick={() => abrir(false)}><Icons.Mail size={16} /> Abrir en mi correo</Btn>}
          {correo && app !== "predeterminada" && <Btn tone="blue" testid={`ocurrencia-gmail-${o.id}`} onClick={() => abrir(true)}><Icons.Mail size={16} /> Abrir en Gmail</Btn>}
          {correo && <Btn testid={`ocurrencia-copiar-${o.id}`} onClick={copiar}><Icons.Copy size={16} /> Copiar mensaje</Btn>}
          <Btn tone="green" testid={`ocurrencia-realizada-${o.id}`} onClick={() => accion("realizada", {}, "Marcada como realizada")}><Icons.Check size={16} /> Realizada</Btn>
          <Btn tone="amber" testid={`ocurrencia-posponer-${o.id}`} onClick={() => setPos(true)}>Posponer</Btn>
          <Btn testid={`ocurrencia-omitir-${o.id}`} onClick={() => accion("omitir", {}, "Omitida esta vez")}>Omitir esta vez</Btn>
        </div>
      )}
    </div>
  );
};

export const useOcurrencias = () => {
  const [ocs, setOcs] = useState([]);
  const cargar = useCallback(() => api.get("/gestiones/ocurrencias").then((r) => setOcs(r.data)).catch(() => {}), []);
  useEffect(() => {
    cargar();
    window.addEventListener("aldia-gestiones", cargar);
    return () => window.removeEventListener("aldia-gestiones", cargar);
  }, [cargar]);
  return [ocs, () => { cargar(); window.dispatchEvent(new Event("aldia-gestiones")); }];
};

// Bloque de Inicio: solo si hay gestiones de hoy (o pospuestas que ya tocan).
export const GestionesHoy = () => {
  const [ocs, cargar] = useOcurrencias();
  if (!ocs.length) return null;
  return (
    <section className="space-y-3" data-testid="home-gestiones-hoy">
      <h2 className="font-heading text-lg sm:text-xl font-bold text-[#1C2421] flex items-center gap-2"><Icons.Repeat size={20} className="text-[#3A6B56]" /> Pagos y recordatorios de hoy</h2>
      {ocs.map((o) => <OcurrenciaCard key={o.id} o={o} onChange={cargar} />)}
    </section>
  );
};
